import constant from '../constants/constant';

const MEASUREMENT_ID = constant.GA4_MEASUREMENT_ID;
const NOTICE_COOKIE = 'viberary_cookie_notice';
const NOTICE_ACCEPTED = 'accepted';
const ALLOWED_EVENTS = [
  'track_impression',
  'track_card_click',
  'audio_play',
  'audio_pause',
  'download_page_view',
  'download_click',
  'filter_apply',
  'load_more_click',
  'pagination_click',
  'related_track_click'
];
const ALLOWED_PARAMS = [
  'track_id',
  'slug',
  'track_slug',
  'download_variant',
  'list_context',
  'device_class',
  'filter_type',
  'category_type',
  'category_slug',
  'tempo',
  'route_name',
  'page_path'
];

let configured = false;
let lastPagePath = null;

function isProductionHost() {
  return /(^|\.)viberary\.com$/i.test(window.location.hostname)
    && !window.location.hostname.includes('preview-7q9');
}

function cookieDomainPart() {
  return /\.viberary\.com$/i.test(`.${window.location.hostname}`)
    ? '; domain=.viberary.com'
    : '';
}

function writeCookie(name, value) {
  const maxAge = 60 * 60 * 24 * 180;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/${cookieDomainPart()}; SameSite=Lax; Secure`;
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getCookieNoticeAcknowledged() {
  return readCookie(NOTICE_COOKIE) === NOTICE_ACCEPTED;
}

function consentState(analyticsStorage) {
  return {
    analytics_storage: analyticsStorage,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  };
}

function setConsentDefaults() {
  window.gtag('consent', 'default', Object.assign(
    consentState('granted')
  ));
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };
}

function ensureScript() {
  if (!isProductionHost() || !MEASUREMENT_ID || document.getElementById('viberary-ga4-script')) {
    return;
  }
  const script = document.createElement('script');
  script.id = 'viberary-ga4-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

function ensureConfigured() {
  if (configured || !window.gtag || !MEASUREMENT_ID || !isProductionHost()) {
    return;
  }
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    cookie_domain: 'auto'
  });
  configured = true;
}

function currentPath(route) {
  const browserPath = currentBrowserPath();
  if (route && route.fullPath) {
    if (route.fullPath === '/' && browserPath !== '/') {
      return browserPath;
    }
    return route.fullPath;
  }
  return browserPath;
}

function currentBrowserPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isSearchRoute(route) {
  const routePath = route && route.fullPath ? route.fullPath : '';
  const browserPath = currentBrowserPath();
  return (route && route.name === 'HomeSearch')
    || routePath.indexOf('/search/') === 0
    || browserPath.indexOf('/search/') === 0;
}

function analyticsPath(route) {
  const path = currentPath(route);
  if (isSearchRoute(route)) {
    return '/search';
  }
  return path;
}

function analyticsTitle(route) {
  if (isSearchRoute(route)) {
    return 'Search | Viberary';
  }
  return document.title;
}

function analyticsReferrer() {
  if (!document.referrer) {
    return undefined;
  }
  try {
    const referrer = new URL(document.referrer);
    if (/(^|\.)viberary\.com$/i.test(referrer.hostname) && referrer.pathname.indexOf('/search/') === 0) {
      return `${referrer.origin}/search`;
    }
    return document.referrer;
  } catch (error) {
    return undefined;
  }
}

export function trackPageView(route) {
  if (!isProductionHost()) {
    return;
  }
  ensureDataLayer();
  ensureConfigured();
  const path = analyticsPath(route);
  if (path === lastPagePath) {
    return;
  }
  lastPagePath = path;
  window.gtag('event', 'page_view', {
    send_to: MEASUREMENT_ID,
    page_title: analyticsTitle(route),
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_referrer: analyticsReferrer(),
    route_name: route && route.name ? route.name : undefined
  });
}

function sanitizeParams(params = {}) {
  const clean = {};
  ALLOWED_PARAMS.forEach((key) => {
    if (params[key] === undefined || params[key] === null) {
      return;
    }
    if (typeof params[key] === 'string') {
      clean[key] = params[key].slice(0, 100);
    } else if (typeof params[key] === 'number' || typeof params[key] === 'boolean') {
      clean[key] = params[key];
    }
  });
  return clean;
}

export function trackAnalyticsEvent(eventName, params = {}) {
  if (!ALLOWED_EVENTS.includes(eventName) || !isProductionHost()) {
    return;
  }
  ensureDataLayer();
  ensureConfigured();
  window.gtag('event', eventName, sanitizeParams(params));
}

export function setCookieNoticeAcknowledged() {
  writeCookie(NOTICE_COOKIE, NOTICE_ACCEPTED);
  ensureDataLayer();
  ensureConfigured();
  window.gtag('consent', 'update', consentState('granted'));
  window.dispatchEvent(new CustomEvent('viberary:cookie-notice', { detail: NOTICE_ACCEPTED }));
}

export function openCookieNotice() {
  window.dispatchEvent(new Event('viberary:open-cookie-notice'));
}

export function getAnalyticsConsent() {
  return 'granted';
}

export function hasAnalyticsConsent() {
  return true;
}

export function setAnalyticsConsent() {
  setCookieNoticeAcknowledged();
}

export function openAnalyticsChoices() {
  openCookieNotice();
}

export function initViberaryGa4(router) {
  ensureDataLayer();
  setConsentDefaults();
  window.gtag('js', new Date());
  ensureScript();
  ensureConfigured();

  window.viberaryCookieNotice = {
    get: getCookieNoticeAcknowledged,
    accept: setCookieNoticeAcknowledged,
    open: openCookieNotice
  };
  window.viberaryAnalyticsConsent = {
    get: () => 'granted',
    set: setCookieNoticeAcknowledged,
    open: openCookieNotice
  };

  trackPageView(router.currentRoute);

  router.afterEach((to, from) => {
    if (!from || to.fullPath !== from.fullPath) {
      setTimeout(() => trackPageView(to), 0);
    }
  });
}

export {
  MEASUREMENT_ID,
  NOTICE_COOKIE,
  NOTICE_ACCEPTED
};
